import { compose, cva } from "cva";
import {
  Radio as RACRadio,
  RadioGroup as RACRadioGroup,
  RadioGroupProps as RACRadioGroupProps,
  RadioProps,
  ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Description, FieldError, Label } from "./Field";
import { composeTailwindRenderProps, focusRing } from "./utils";

export interface RadioGroupProps extends Omit<RACRadioGroupProps, "children"> {
  label?: string;
  children?: React.ReactNode;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isSelected?: boolean;
  value?: string;
  disabledClassname?: string;
  internalClassname?: string;
  border?: boolean;
}

const radioGroupStyles = cva({
  base: "",
  variants: {
    border: {
      true: "border border-theme-outline-variant flex-col rounded-sm flex gap-2",
      false: "flex gap-2",
    },
  },
  defaultVariants: {
    border: true,
  },
});

export function RadioGroup(props: RadioGroupProps) {
  return (
    <RACRadioGroup
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-2",
      )}
    >
      <Label>{props.label}</Label>
      <div className={radioGroupStyles({ border: props.border })}>
        {props.children}
      </div>
      {props.description && <Description>{props.description}</Description>}
      <FieldError>{props.errorMessage}</FieldError>
    </RACRadioGroup>
  );
}

const styles = cva({
  base: "w-6 h-6 rounded-full border-2 transition-all",
  variants: {
    isSelected: {
      false: "border-theme-outline",
      true: "before:block before:w-3 before:h-3 before:rounded-full before:bg-theme-active before:left-1/2 before:top-1/2 before:-translate-y-1/2 before:-translate-x-1/2 before:absolute relative border-theme-active",
    },
    isInvalid: {
      true: "border-red-700 dark:border-red-600 group-pressed:border-red-800 dark:group-pressed:border-red-700 forced-colors:border-[Mark]!",
    },
    isDisabled: {
      true: "border-gray-200 dark:border-zinc-700 forced-colors:border-[GrayText]!",
    },
  },
});

export function Radio(props: RadioProps) {
  return (
    <RACRadio
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group text-theme-on-background-variant disabled:text-theme-on-background-variant/50 flex items-center gap-2 text-sm transition",
      )}
    >
      {(renderProps) => (
        <>
          <div className={compose(styles, focusRing)(renderProps)} />
          {props.children}
        </>
      )}
    </RACRadio>
  );
}

export function CustomRadio(props: RadioGroupProps) {
  return (
    <label
      htmlFor={props.value ?? undefined}
      className={twMerge(
        "flex cursor-pointer items-center gap-2",
        props.isDisabled && "cursor-not-allowed opacity-50",
      )}
    >
      <div className="grid place-items-center">
        <input
          type="radio"
          id={props.value ?? undefined}
          className={twMerge(
            "peer border-theme-outline-variant disabled:border-theme-outline checked:border-theme-active col-start-1 row-start-1 h-6 w-6 shrink-0 cursor-pointer appearance-none rounded-full border-2 transition-all duration-350",
            props.disabledClassname ?? "",
          )}
          checked={props.isSelected ?? false}
          onChange={() =>
            !props.isDisabled &&
            props.value !== undefined &&
            props.onChange?.(props.value)
          }
          disabled={props.isDisabled}
        />
        <div
          className={twMerge(
            "peer-checked:bg-theme-active pointer-events-none col-start-1 row-start-1 h-3 w-3 rounded-full transition-all duration-350 peer-checked:peer-disabled:bg-gray-400",
            props.internalClassname ?? "",
          )}
        />
      </div>
    </label>
  );
}
